import { Instance, SnapshotOut, types } from 'mobx-state-tree';
import { IUnifiedFilter } from 'src/contexts/SearchFilterContext';
import { DashboardApi, TGetFsResult, TGetSpeedResult, TGetWeightResult } from 'src/services';

import { createFailSuccess, IFailSuccess } from '../dashboard/fail-success/FailSuccess';
import { createSpeed, ISpeed } from '../dashboard/speed/Speed';
import { createWeight, IWeight } from '../dashboard/weight/Weight';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { IProjectFilter } from '../project/Project';

/**
 * # DashboardStore
 *
 * DashboardStore을 설명하세요.
 */
export const DashboardStore = types
  .model('DashboardStore')
  // --------------------------------------------------------------------------
  .props({
    dashboards: createWeight() || createSpeed(),
    margin: createWeight(),
    size: createWeight(),
    speed: createSpeed(),
    fs: createFailSuccess(),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    /**
     * dashboards을 교체
     *
     * @param `dashboards` 새로운 모델의 배열
     */
    setDashboards: (dashboards: IWeight | ISpeed | IFailSuccess) => {
      self.dashboards = dashboards as any;
    },
    setMargin: (dashboards: IWeight) => {
      self.margin = dashboards;
    },
    setSize: (dashboards: IWeight) => {
      self.size = dashboards;
    },
    setSpeed: (dashboards: ISpeed) => {
      self.speed = dashboards;
    },
    setFs: (dashboards: IFailSuccess) => {
      self.fs = dashboards;
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    getMargin: async (type: string, options?: { deptSid?: number; filter?: IUnifiedFilter }) => {
      const { deptSid, filter } = options || {};
      const dashboardApi: DashboardApi = new DashboardApi(self.environment.api);
      const result: TGetWeightResult = await dashboardApi.getMargin(type, deptSid, filter);
      self.rootStore.responseStore.getResponseResult(result);

      if (result.kind === 'ok') {
        self.setDashboards(result.dashboards);
        self.setMargin(result.dashboards);
      } else {
        console.error(result.kind);
      }
    },

    getSize: async (type: string, options?: { deptSid?: number; filter?: IUnifiedFilter }) => {
      const { deptSid, filter } = options || {};
      const dashboardApi: DashboardApi = new DashboardApi(self.environment.api);
      const result: TGetWeightResult = await dashboardApi.getSize(type, deptSid, filter);
      self.rootStore.responseStore.getResponseResult(result);

      if (result.kind === 'ok') {
        self.setDashboards(result.dashboards);
        self.setSize(result.dashboards);
      } else {
        console.error(result.kind);
      }
    },

    getSpeed: async (
      quarterNum: number,
      options?: { deptSid?: number; filter?: IUnifiedFilter },
    ) => {
      const { deptSid, filter } = options || {};
      const dashboardApi: DashboardApi = new DashboardApi(self.environment.api);
      const result: TGetSpeedResult = await dashboardApi.getSpeed(quarterNum, deptSid, filter);
      self.rootStore.responseStore.getResponseResult(result);

      if (result.kind === 'ok') {
        self.setDashboards(result.dashboards);
        self.setSpeed(result.dashboards);
      } else {
        console.error(result.kind);
      }
    },

    getFs: async (quarter: number, options?: { deptSid?: number; filter?: IUnifiedFilter }) => {
      const { deptSid, filter } = options || {};
      const dashboardApi: DashboardApi = new DashboardApi(self.environment.api);
      const result: TGetFsResult = await dashboardApi.getFs(quarter, deptSid, filter);
      self.rootStore.responseStore.getResponseResult(result);

      if (result.kind === 'ok') {
        self.setDashboards(result.dashboards);
        self.setFs(result.dashboards);
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TDashboardStore = Instance<typeof DashboardStore>;
type TDashboardStoreSnapshot = SnapshotOut<typeof DashboardStore>;

export interface IDashboardStore extends TDashboardStore {}
export type TDashboardStoreKeys = keyof TDashboardStoreSnapshot & string;
export interface IDashboardStoreSnapshot extends TDashboardStoreSnapshot {}
export const createDashboardStore = () => types.optional(DashboardStore, {} as TDashboardStore);
